import React from "react";
import { MapPin, Loader2, RefreshCcw, Check } from "lucide-react";

import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Command,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "../ui/command";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import { cn } from "../../utils/lib";

import { useDebounce } from "../../hooks/use-debounce";

import {
  placesDetailsSource,
  placesAutoComplete,
} from "../../api/pricing/action";

export function LocationSearch({
  id,
  value,
  htmlFor,
  onChange,
  onSelect,
  className,
  placeholder,
}) {
  const [data, setData] = React.useState([]); // <FetchCallLocation[]>
  const [error, setError] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  //   // this state is for second api call,
  const [isLoading2, setIsLoading2] = React.useState(false);
  const [isError2, setIsError2] = React.useState(false);
  const [isSuccess2, setIsSuccess2] = React.useState(false);
  const [data2, setData2] = React.useState({
    description: "",
    place_id: "",
  }); // <FetchCallLocation >

  const [clearedData, setClearedData] = React.useState(false);

  const debouncedQuery = useDebounce(value, 500);

  // Fetch autocomplete results
  const fetchDropdownData = React.useCallback(async () => {
    if (debouncedQuery.length < 3) return;

    setLoading(true);
    setError("");
    // setFetched(false);

    try {
      const results = await placesAutoComplete(debouncedQuery);
      setData(results);
      // setFetched(true);
    } catch (err) {
      setError("Failed to fetch data");
      // setFetched(false);
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery]);

  React.useEffect(() => {
    fetchDropdownData();
  }, [fetchDropdownData]);

  React.useEffect(() => {
    if (!open) {
      setData([]);
      setError("");
    }
  }, [open]);

  React.useEffect(() => {
    if (value === "") {
      setIsSuccess2(false);
    }
  }, [value]);

  // Fetch place details when a user selects an item
  const handleSelect = async (place) => {
    // place: FetchCallLocation
    setIsError2(false);
    setIsLoading2(true);
    setIsSuccess2(false);
    setClearedData(true);

    try {
      const details = await placesDetailsSource(place.place_id);

      onSelect({
        lat: details.result.lat,
        lng: details.result.lng,
        place_id: place.place_id,
        address: place.description,
      });

      setIsError2(false);
      setIsSuccess2(true);
      setData([]);
    } catch (err) {
      setIsError2(true);
    } finally {
      setIsLoading2(false);
    }
  };

  return (
    <div className={cn("relative", className)}>
      <Input
        id={id}
        value={value}
        autoComplete="off"
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onBlur={() =>
          setTimeout(() => {
            setData([]);
            setOpen(false);
            setClearedData(true);
          }, 300)
        }
        onChange={(e) => {
          setClearedData(false);
          setIsSuccess2(false);
          setIsError2(false);
          setData2({
            description: "",
            place_id: "",
          });
          onChange({
            lat: null,
            lng: null,
            place_id: "",
            address: e.target.value,
          });
        }}
        className="focus-visible:ring-offset-0 bg-[#131821] hover:bg-[#2b3446] hover:text-white text-white border border-[#353C4A] block w-full truncate ring-1 ring-transparent placeholder:truncate focus:outline-none focus-visible:border disabled:border disabled:opacity-90 disabled:bg-[#181E29] disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-black px-8"
      />

      <Label
        htmlFor={htmlFor}
        className="absolute left-3 top-1/2 -translate-y-1/2 hover:cursor-pointer"
      >
        <MapPin className="size-4 text-[#B3B3B3]" />
      </Label>

      {/* Dropdown UI (Only Opens When Needed) */}
      {open && (
        <div className="absolute top-[calc(100%_+_0.5rem)] z-30 border border-[#353C4A] w-full rounded-md bg-popover text-white shadow-sm">
          <Command>
            <CommandList className="bg-[#181E29] text-white focus-visible:ring-offset-0 max-h-40 overflow-y-auto scrollbar-none">
              {/* Loading State */}
              {loading && (
                <CommandEmpty>
                  <div className="flex items-center justify-center">
                    <Loader2 className="size-4 animate-spin" />
                  </div>
                </CommandEmpty>
              )}

              {/* Error State */}
              {error && !clearedData && (
                <CommandEmpty>
                  <div className="flex flex-col items-center justify-center gap-2">
                    <p className="font-medium text-red-500">{error}</p>
                  </div>
                </CommandEmpty>
              )}

              {/* No Results */}
              {debouncedQuery.length >= 3 &&
                data.length === 0 &&
                !error &&
                !loading &&
                !clearedData && <CommandEmpty>No results found.</CommandEmpty>}

              {/* Default Message */}
              {(clearedData || debouncedQuery.length < 3) && (
                <CommandEmpty>Type to search</CommandEmpty>
              )}

              {/* Data List */}
              {!error && !loading && data.length > 0 && !clearedData && (
                <CommandGroup>
                  {data.map((place) => (
                    <CommandItem
                      key={place.place_id}
                      onSelect={() => {
                        setData2(place);
                        handleSelect(place);
                      }}
                      className="font-medium hover:cursor-pointer"
                    >
                      {place.description}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      )}

      {/* Icons for Second Fetch Call */}
      {isLoading2 && (
        <div className="absolute right-3 top-1/2 flex -translate-y-1/2  items-center justify-center">
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Loader2 className=" size-4 animate-spin hover:cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="border border-transparent bg-slate-700 text-xs text-white"
              >
                <p>Validating selected City...</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {isSuccess2 && (
        <div className="absolute right-3 top-1/2 flex -translate-y-1/2  items-center justify-center">
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Check className="size-4 text-green-500 hover:cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="border border-transparent bg-slate-700 text-xs text-white"
              >
                <p>City Name is Valid</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {isError2 && (
        <div
          className="absolute right-3 top-1/2 flex -translate-y-1/2  items-center justify-center"
          onClick={() => handleSelect(data2)}
        >
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <RefreshCcw className="size-4 text-red-500 hover:cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="border border-transparent bg-slate-700 text-xs text-white"
              >
                <p>Error Occurred while Validating city</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}
